import React, { useState, useEffect, useRef } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   Button,
   FormControl,
   FormLabel,
   Input,
   VStack,
   Grid,
   useColorMode,
   Stack,
   Text,
   Flex,
   useColorModeValue,
   Box,
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { getDaybook } from '../../../../../redux/actions/daybook';
import { getAccount } from '../../../../../redux/actions/account';
import { renderDynamicDropdown } from 'components/ui/DynamicDropdown';
import MultiSelectDropdown from 'components/ui/MultiSelectDropdown';
import { useUser, useGlobal } from 'contexts';
import AddEditForm from '../../../masters/Pages/AllAccountMaster/AddEditFormForAccount';

const SalesInvoiceFilterModal = ({
   isOpen,
   onClose,
   onApplyFilters,
   initialFilters,
   entityType,
   Totals = {},
   status = true,
}) => {
   const dispatch = useDispatch();
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const textColor = isDark ? 'white' : '#9E9E9E';
   const isColorMode = useColorModeValue(true, false);
   const companyId = Number(localStorage.getItem('companyId'));
   const daybooks = useSelector((state) => state.daybook?.list || []);
   const accounts = useSelector((state) => state.account?.list || []);
   const [sortBy, setSortBy] = useState(initialFilters?.sortBy || 'id');
   const [sortOrder, setSortOrder] = useState(initialFilters?.sortOrder || 'DESC');
   const safeTotals = {
      totalFullPending: 0,
      totalPartPending: 0,
      totalClear: 0,
      ...Totals,
   };
   const [errors, setErrors] = useState({});
   const { userGlobalSettingsData } = useGlobal();
   const globalSetting = userGlobalSettingsData?.[0]?.askCompany?.account;
   const shouldShowCompanyDropdown = globalSetting || false;

   // Focus management refs
   const fullPendingRef = useRef(null);
   const partPendingRef = useRef(null);
   const clearRef = useRef(null);
   const fromDateRef = useRef(null);
   const docNoRef = useRef(null);
   const toDateRef = useRef(null);
   const daybookRef = useRef(null);
   const accountRef = useRef(null);
   const clearButtonRef = useRef(null);
   const applyButtonRef = useRef(null);
   const soldRef = useRef(null);
   const receivedOnlyRef = useRef(null);
   const allRef = useRef(null);
   const issueToVendorRef = useRef(null);
   const purchaseOnlyRef = useRef(null);
   const purchaseReturnRef = useRef(null);
   const issuedToVendorRef = useRef(null);
   const receivedFromVendorRef = useRef(null);
   const issueReturnRef = useRef(null);

   const focusableElements =
      entityType === 'receivemaster'
         ? [
            soldRef,
            receivedOnlyRef,
            issuedToVendorRef,
            receivedFromVendorRef,
            issueReturnRef,
            fromDateRef,
            toDateRef,
            daybookRef,
            accountRef,
            clearButtonRef,
            applyButtonRef,
         ]
         : entityType === 'purchaseinvoice'
            ? [
               allRef,
               soldRef,
               receivedFromVendorRef,
               issueToVendorRef,
               purchaseOnlyRef,
               purchaseReturnRef,
               issueReturnRef,
               fromDateRef,
               toDateRef,
               daybookRef,
               accountRef,
               clearButtonRef,
               applyButtonRef,
            ]
            : [
               fullPendingRef,
               partPendingRef,
               clearRef,
               fromDateRef,
               toDateRef,
               daybookRef,
               accountRef,
               clearButtonRef,
               applyButtonRef,
            ];

   const [currentFocusIndex, setCurrentFocusIndex] = useState(0);
   const [currentIssueFocusIndex, setCurrentIssueFocusIndex] = useState(0);
   const [daybookIds, setDaybookIds] = useState([]);
   const [accountIds, setAccountIds] = useState([]);

   const [filters, setFilters] = useState({
      paymentStatus: initialFilters?.paymentstatus || 'all',
      issueStatus: initialFilters?.issueStatus || 'all',
      purchaseStatus: initialFilters?.purchaseStatus || 'all',
      invoiceBook: initialFilters?.invoiceBook || 'all',
      duration: initialFilters?.duration || 'any',
      contact: initialFilters?.contact || '',
      item: initialFilters?.item || '',
      daybookIds: initialFilters?.daybookIds || [],
      docno: initialFilters?.docno || null,
      accountIds: initialFilters?.accountIds || [],
      fromDate: initialFilters?.fromDate || '',
      toDate: initialFilters?.toDate || '',
      sortBy: initialFilters?.sortBy || 'id',
      sortOrder: initialFilters?.sortOrder || 'DESC',
   });

   // Get daybook type based on the filter type
   const getDaybookType = (entityType) => {
      switch (entityType) {
         case 'salechallan':
            return 4;
         case 'saleinvoice':
            return 5;
         case 'salereturn':
            return 6;
         case 'purchasechallan':
            return 10;
         case 'purchaseinvoice':
            return 11;
         case 'purchasereturn':
            return 12;
         case 'issuemaster':
            return 19;
         case 'receivemaster':
            return 20;
         default:
            return;
      }
   };
   const { user } = useUser();
   const daybookType = getDaybookType(entityType);
   useEffect(() => {
      if (!isOpen || !companyId) return;

      // const daybookType = getDaybookType(entityType);

      if (user?.loginhistory?.companyId) {
         // dispatch(getDaybook({ companyId: user.loginhistory.companyId, type: daybookType }));
         dispatch(
            getAccount({
               compId: user?.loginhistory?.companyId,
               status: true,
               accountTypes: ['K', 'E', 'C', 'D', 'BROKER', 'I', 'L', 'M', 'N', 'A'].join(','),
               ...(shouldShowCompanyDropdown && { companyIds: user?.loginhistory?.companyId }),
            }),
         );
      }
   }, [isOpen, companyId, entityType, user, dispatch]);

   // Focus the first element when modal opens
   useEffect(() => {
      if (isOpen) {
         setTimeout(() => {
            setCurrentFocusIndex(0);
            focusableElements[0]?.current?.focus();
         }, 100);
      }
   }, [isOpen]);

   // Handle keyboard navigation
   const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
         e.preventDefault();
         onClose();
         return;
      }

      if (e.key === 'Tab' || e.key === 'Enter') {
         e.preventDefault();
         const nextIndex = (currentFocusIndex + 1) % focusableElements.length;
         setCurrentFocusIndex(nextIndex);
         focusableElements[nextIndex]?.current?.focus();
      }
   };

   // Add event listener for keyboard navigation
   useEffect(() => {
      if (isOpen) {
         document.addEventListener('keydown', handleKeyDown);
         return () => {
            document.removeEventListener('keydown', handleKeyDown);
         };
      }
   }, [isOpen, currentFocusIndex]);

   const handleAccountChange = (selectedOptions) => {
      setAccountIds(selectedOptions ? selectedOptions : []);
   };

   const handleDaybookChange = (selectedOptions) => {
      setDaybookIds(selectedOptions ? selectedOptions : []);
   };

   const handleChange = (e) => {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
   };

   const handleDateChange = (e) => {
      const { name, value } = e.target;
      setFilters((prev) => ({
         ...prev,
         [name]: value,
      }));
   };

   const handlePaymentStatusChange = (status) => {
      setFilters((prev) => ({
         ...prev,
         paymentStatus: status,
      }));
   };

   const handleIssueStatusChange = (status) => {
      setFilters((prev) => ({
         ...prev,
         issueStatus: status,
      }));
   };

   const handlePurchaseStatusChange = (status) => {
      setFilters((prev) => ({
         ...prev,
         purchaseStatus: status,
      }));
   };

   const handleSortChange = (field) => {
      let newSortOrder = 'DESC';

      if (sortBy === field) {
         newSortOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC';
      } else {
         newSortOrder = 'DESC';
      }

      const newSortBy = field;

      setSortBy(newSortBy);
      setSortOrder(newSortOrder);

      setFilters((prev) => ({
         ...prev,
         sortBy: newSortBy,
         sortOrder: newSortOrder,
      }));
   };

   const handleApply = () => {
      const payload = {
         ...filters,
         daybookIds: Array.isArray(daybookIds) ? daybookIds : [daybookIds].filter(Boolean),
         accountIds: Array.isArray(accountIds) ? accountIds : [accountIds].filter(Boolean),
         sortBy,
         sortOrder,
      };

      console.log('Filter modal payload:', payload); // Debug log

      onApplyFilters(payload);
      onClose();
   };

   const handleClear = () => {
      const clearedFilters = {
         paymentStatus: 'all',
         purchaseStatus: 'all',
         issueStatus: 'all',
         invoiceBook: 'all',
         duration: 'any',
         contact: '',
         item: '',
         docno: null,
         daybookIds: [],
         accountIds: [],
         fromDate: '',
         toDate: '',
         sortBy: 'id',
         sortOrder: 'DESC',
      };
      setFilters(clearedFilters);
      setDaybookIds([]);
      setAccountIds([]);
      setSortBy('id');
      setSortOrder('DESC');
      onApplyFilters(clearedFilters);
      onClose();
   };

   // Focus indicator styles
   const getFocusStyles = (index) => ({
      outline: currentFocusIndex === index ? '2px solid #3182ce' : 'none',
      outlineOffset: '2px',
      boxShadow: currentFocusIndex === index ? '0 0 0 3px rgba(66, 153, 225, 0.6)' : 'none',
      transition: 'all 0.2s ease-in-out',
   });

   const getIssueFocusStyles = (index) => ({
      outline: currentIssueFocusIndex === index ? '2px solid #3182ce' : 'none',
      outlineOffset: '2px',
      boxShadow: currentIssueFocusIndex === index ? '0 0 0 3px rgba(66, 153, 225, 0.6)' : 'none',
      transition: 'all 0.2s ease-in-out',
   });

   return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
         <ModalOverlay className="backdrop-blur-sm bg-black/40" />
         <ModalContent
            className={`${isDark ? 'bg-gray-800' : 'bg-white'} shadow-2xl rounded-2xl border ${isDark ? 'border-gray-700' : 'border-gray-100'
               }`}>
            <ModalHeader
               className={`${isDark
                  ? 'bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800 border-gray-700'
                  : 'bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-gray-200'
                  } border-b rounded-t-2xl`}>
               <Flex alignItems="center" gap={3}>
                  <div
                     className={`w-10 h-10 rounded-xl ${isDark ? 'bg-blue-600' : 'bg-blue-500'
                        } flex items-center justify-center shadow-lg`}>
                     <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                        />
                     </svg>
                  </div>
                  <Text className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                     Advanced Filters
                  </Text>
               </Flex>
            </ModalHeader>
            <ModalCloseButton
               className={`${isDark
                  ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  } rounded-lg transition-all duration-200 mt-2 mr-2`}
            />
            <ModalBody className={`py-6 px-6 ${isDark ? 'bg-gray-800' : 'bg-gradient-to-b from-gray-50 to-white'}`}>
               <VStack spacing={3} align="stretch">
                  {/* Payment Status */}
                  {status && (
                     <div
                        className={`px-4 p-3 rounded-xl ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-white border border-gray-200'
                           } shadow-sm`}>
                        <Stack spacing={3}>
                           {entityType !== 'issuemaster' && (
                              <Text
                                 className={`font-semibold text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'
                                    } uppercase tracking-wide flex items-center gap-2`}>
                                 <span
                                    className={`w-1 h-4 rounded-full ${isDark ? 'bg-blue-500' : 'bg-blue-600'}`}></span>
                                 Payment Status
                              </Text>
                           )}
                           {entityType === 'issuemaster' && (
                              <Text
                                 className={`font-semibold text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'
                                    } uppercase tracking-wide flex items-center gap-2`}>
                                 <span
                                    className={`w-1 h-4 rounded-full ${isDark ? 'bg-blue-500' : 'bg-blue-600'}`}></span>
                                 Stock Status
                              </Text>
                           )}
                           <Flex flexWrap="wrap" gap={2}>
                              {entityType === 'receivemaster' ? (
                                 <>
                                    <Button
                                       ref={soldRef}
                                       variant={filters.paymentStatus === 'SOLD' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.paymentStatus === 'SOLD'
                                          ? `${isDark
                                             ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                             : 'bg-blue-500 hover:bg-blue-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePaymentStatusChange('SOLD')}
                                       style={getFocusStyles(0)}
                                       onFocus={() => setCurrentFocusIndex(0)}>
                                       <span className="font-medium">Sold</span>
                                       <span
                                          className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${filters.paymentStatus === 'SOLD'
                                             ? 'bg-white/20'
                                             : isDark
                                                ? 'bg-gray-800'
                                                : 'bg-gray-100'
                                             }`}>
                                          {safeTotals.sold || 0}
                                       </span>
                                    </Button>
                                    <Button
                                       ref={receivedOnlyRef}
                                       variant={filters.paymentStatus === 'RECEIVED_ONLY' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.paymentStatus === 'RECEIVED_ONLY'
                                          ? `${isDark
                                             ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                             : 'bg-blue-500 hover:bg-blue-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePaymentStatusChange('RECEIVED_ONLY')}
                                       style={getFocusStyles(1)}
                                       onFocus={() => setCurrentFocusIndex(1)}>
                                       <span className="font-medium">Received Only</span>
                                       <span
                                          className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${filters.paymentStatus === 'RECEIVED_ONLY'
                                             ? 'bg-white/20'
                                             : isDark
                                                ? 'bg-gray-800'
                                                : 'bg-gray-100'
                                             }`}>
                                          {safeTotals.receivedOnly || 0}
                                       </span>
                                    </Button>
                                    <Button
                                       ref={issuedToVendorRef}
                                       variant={filters.paymentStatus === 'ISSUED_TO_VENDOR' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.paymentStatus === 'ISSUED_TO_VENDOR'
                                          ? `${isDark
                                             ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                             : 'bg-blue-500 hover:bg-blue-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePaymentStatusChange('ISSUED_TO_VENDOR')}
                                       style={getFocusStyles(2)}
                                       onFocus={() => setCurrentFocusIndex(2)}>
                                       <span className="font-medium">Issued to Vendor</span>
                                       <span
                                          className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${filters.paymentStatus === 'ISSUED_TO_VENDOR'
                                             ? 'bg-white/20'
                                             : isDark
                                                ? 'bg-gray-800'
                                                : 'bg-gray-100'
                                             }`}>
                                          {safeTotals.issuedToVendor || 0}
                                       </span>
                                    </Button>
                                    <Button
                                       ref={receivedFromVendorRef}
                                       variant={filters.paymentStatus === 'RECEIVED_FROM_VENDOR' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.paymentStatus === 'RECEIVED_FROM_VENDOR'
                                          ? `${isDark
                                             ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                             : 'bg-blue-500 hover:bg-blue-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePaymentStatusChange('RECEIVED_FROM_VENDOR')}
                                       style={getFocusStyles(3)}
                                       onFocus={() => setCurrentFocusIndex(3)}>
                                       <span className="font-medium">Received from Vendor</span>
                                       <span
                                          className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${filters.paymentStatus === 'RECEIVED_FROM_VENDOR'
                                             ? 'bg-white/20'
                                             : isDark
                                                ? 'bg-gray-800'
                                                : 'bg-gray-100'
                                             }`}>
                                          {safeTotals.receivedFromVendor || 0}
                                       </span>
                                    </Button>
                                    <Button
                                       ref={issueReturnRef}
                                       variant={filters.paymentStatus === 'ISSUE_RETURN' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.paymentStatus === 'ISSUE_RETURN'
                                          ? `${isDark
                                             ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                             : 'bg-blue-500 hover:bg-blue-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePaymentStatusChange('ISSUE_RETURN')}
                                       style={getFocusStyles(4)}
                                       onFocus={() => setCurrentFocusIndex(4)}>
                                       <span className="font-medium">Issue Return</span>
                                       <span
                                          className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${filters.paymentStatus === 'ISSUE_RETURN'
                                             ? 'bg-white/20'
                                             : isDark
                                                ? 'bg-gray-800'
                                                : 'bg-gray-100'
                                             }`}>
                                          {safeTotals.issueReturn || 0}
                                       </span>
                                    </Button>
                                 </>
                              ) : entityType === 'issuemaster' ? (
                                 <>
                                    <Button
                                       ref={soldRef}
                                       variant={filters.paymentStatus === 'Party Stock' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.paymentStatus === 'Party Stock'
                                          ? `${isDark
                                             ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                             : 'bg-blue-500 hover:bg-blue-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePaymentStatusChange('Party Stock')}
                                       style={getFocusStyles(0)}
                                       onFocus={() => setCurrentFocusIndex(0)}>
                                       <span className="font-medium">Party Stock</span>
                                       <span
                                          className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${filters.paymentStatus === 'Party Stock'
                                             ? 'bg-white/20'
                                             : isDark
                                                ? 'bg-gray-800'
                                                : 'bg-gray-100'
                                             }`}>
                                          {safeTotals.partystock || 0}
                                       </span>
                                    </Button>
                                    <Button
                                       ref={receivedOnlyRef}
                                       variant={filters.paymentStatus === 'STORE STOCK' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.paymentStatus === 'STORE STOCK'
                                          ? `${isDark
                                             ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                             : 'bg-blue-500 hover:bg-blue-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePaymentStatusChange('STORE STOCK')}
                                       style={getFocusStyles(1)}
                                       onFocus={() => setCurrentFocusIndex(1)}>
                                       <span className="font-medium">Store Stock</span>
                                       <span
                                          className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${filters.paymentStatus === 'STORE STOCK'
                                             ? 'bg-white/20'
                                             : isDark
                                                ? 'bg-gray-800'
                                                : 'bg-gray-100'
                                             }`}>
                                          {safeTotals.storestock || 0}
                                       </span>
                                    </Button>
                                 </>
                              ) : (
                                 <>
                                    <Button
                                       ref={fullPendingRef}
                                       variant={filters.paymentStatus === 'FULL PENDING' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.paymentStatus === 'FULL PENDING'
                                          ? `${isDark
                                             ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                             : 'bg-blue-500 hover:bg-blue-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePaymentStatusChange('FULL PENDING')}
                                       style={getFocusStyles(0)}
                                       onFocus={() => setCurrentFocusIndex(0)}>
                                       <span className="font-medium">Full Pending</span>
                                       <span
                                          className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${filters.paymentStatus === 'FULL PENDING'
                                             ? 'bg-white/20'
                                             : isDark
                                                ? 'bg-gray-800'
                                                : 'bg-gray-100'
                                             }`}>
                                          {safeTotals.totalFullPending}
                                       </span>
                                    </Button>
                                    <Button
                                       ref={partPendingRef}
                                       variant={filters.paymentStatus === 'PART PENDING' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.paymentStatus === 'PART PENDING'
                                          ? `${isDark
                                             ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                             : 'bg-blue-500 hover:bg-blue-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePaymentStatusChange('PART PENDING')}
                                       style={getFocusStyles(1)}
                                       onFocus={() => setCurrentFocusIndex(1)}>
                                       <span className="font-medium">Part Pending</span>
                                       <span
                                          className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${filters.paymentStatus === 'PART PENDING'
                                             ? 'bg-white/20'
                                             : isDark
                                                ? 'bg-gray-800'
                                                : 'bg-gray-100'
                                             }`}>
                                          {safeTotals.totalPartPending}
                                       </span>
                                    </Button>
                                    <Button
                                       ref={clearRef}
                                       variant={filters.paymentStatus === 'CLEAR' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.paymentStatus === 'CLEAR'
                                          ? `${isDark
                                             ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                             : 'bg-blue-500 hover:bg-blue-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePaymentStatusChange('CLEAR')}
                                       style={getFocusStyles(2)}
                                       onFocus={() => setCurrentFocusIndex(2)}>
                                       <span className="font-medium">Clear</span>
                                       <span
                                          className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${filters.paymentStatus === 'CLEAR'
                                             ? 'bg-white/20'
                                             : isDark
                                                ? 'bg-gray-800'
                                                : 'bg-gray-100'
                                             }`}>
                                          {safeTotals.totalClear}
                                       </span>
                                    </Button>
                                 </>
                              )}
                           </Flex>
                        </Stack>
                     </div>
                  )}

                  {status && (entityType === 'issuemaster' || entityType === 'purchaseinvoice') && (
                     <div
                        className={`px-4 p-3 rounded-xl ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-white border border-gray-200'
                           } shadow-sm`}>
                        <Stack spacing={3}>
                           {entityType === 'issuemaster' && (
                              <Text
                                 className={`font-semibold text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'
                                    } uppercase tracking-wide flex items-center gap-2`}>
                                 <span
                                    className={`w-1 h-4 rounded-full ${isDark ? 'bg-purple-500' : 'bg-purple-600'
                                       }`}></span>
                                 Issue Status
                              </Text>
                           )}
                           {entityType === 'purchaseinvoice' && (
                              <Text
                                 className={`font-semibold text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'
                                    } uppercase tracking-wide flex items-center gap-2`}>
                                 <span
                                    className={`w-1 h-4 rounded-full ${isDark ? 'bg-purple-500' : 'bg-purple-600'
                                       }`}></span>
                                 Status
                              </Text>
                           )}
                           <Flex flexWrap="wrap" gap={2}>
                              {entityType === 'issuemaster' ? (
                                 <>
                                    <Button
                                       ref={soldRef}
                                       variant={filters.issueStatus === 'ALL' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.issueStatus === 'ALL'
                                          ? `${isDark
                                             ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                             : 'bg-purple-500 hover:bg-purple-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handleIssueStatusChange('ALL')}
                                       style={getIssueFocusStyles(0)}
                                       onFocus={() => setCurrentIssueFocusIndex(0)}>
                                       ALL
                                    </Button>
                                    <Button
                                       ref={receivedOnlyRef}
                                       variant={filters.issueStatus === 'SOLD' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.issueStatus === 'SOLD'
                                          ? `${isDark
                                             ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                             : 'bg-purple-500 hover:bg-purple-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handleIssueStatusChange('SOLD')}
                                       style={getIssueFocusStyles(1)}
                                       onFocus={() => setCurrentIssueFocusIndex(1)}>
                                       SOLD
                                    </Button>
                                    <Button
                                       ref={receivedOnlyRef}
                                       variant={filters.issueStatus === 'RECEIVED_FROM_VENDOR' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.issueStatus === 'RECEIVED_FROM_VENDOR'
                                          ? `${isDark
                                             ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                             : 'bg-purple-500 hover:bg-purple-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handleIssueStatusChange('RECEIVED_FROM_VENDOR')}
                                       style={getIssueFocusStyles(2)}
                                       onFocus={() => setCurrentIssueFocusIndex(2)}>
                                       RECEIVED FROM VENDOR
                                    </Button>
                                    <Button
                                       ref={receivedOnlyRef}
                                       variant={filters.issueStatus === 'ISSUED_TO_VENDOR' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.issueStatus === 'ISSUED_TO_VENDOR'
                                          ? `${isDark
                                             ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                             : 'bg-purple-500 hover:bg-purple-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handleIssueStatusChange('ISSUED_TO_VENDOR')}
                                       style={getIssueFocusStyles(3)}
                                       onFocus={() => setCurrentIssueFocusIndex(3)}>
                                       ISSUED TO VENDOR
                                    </Button>
                                 </>
                              ) : entityType === 'purchaseinvoice' ? (
                                 <>
                                    <Button
                                       ref={allRef}
                                       variant={filters.purchaseStatus === 'all' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.purchaseStatus === 'all'
                                          ? `${isDark
                                             ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                             : 'bg-purple-500 hover:bg-purple-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePurchaseStatusChange('all')}
                                       style={getIssueFocusStyles(0)}
                                       onFocus={() => setCurrentIssueFocusIndex(0)}>
                                       ALL
                                    </Button>
                                    <Button
                                       ref={soldRef}
                                       variant={filters.purchaseStatus === 'SOLD' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.purchaseStatus === 'SOLD'
                                          ? `${isDark
                                             ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                             : 'bg-purple-500 hover:bg-purple-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePurchaseStatusChange('SOLD')}
                                       style={getIssueFocusStyles(1)}
                                       onFocus={() => setCurrentIssueFocusIndex(1)}>
                                       SOLD
                                    </Button>
                                    <Button
                                       ref={receivedFromVendorRef}
                                       variant={filters.purchaseStatus === 'RECEIVED_FROM_VENDOR' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.purchaseStatus === 'RECEIVED_FROM_VENDOR'
                                          ? `${isDark
                                             ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                             : 'bg-purple-500 hover:bg-purple-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePurchaseStatusChange('RECEIVED_FROM_VENDOR')}
                                       style={getIssueFocusStyles(2)}
                                       onFocus={() => setCurrentIssueFocusIndex(2)}>
                                       RECEIVED FROM VENDOR
                                    </Button>
                                    <Button
                                       ref={issueToVendorRef}
                                       variant={filters.purchaseStatus === 'ISSUED_TO_VENDOR' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.purchaseStatus === 'ISSUED_TO_VENDOR'
                                          ? `${isDark
                                             ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                             : 'bg-purple-500 hover:bg-purple-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePurchaseStatusChange('ISSUED_TO_VENDOR')}
                                       style={getIssueFocusStyles(3)}
                                       onFocus={() => setCurrentIssueFocusIndex(3)}>
                                       ISSUED TO VENDOR
                                    </Button>
                                    <Button
                                       ref={purchaseOnlyRef}
                                       variant={filters.purchaseStatus === 'PURCHASE_ONLY' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.purchaseStatus === 'PURCHASE_ONLY'
                                          ? `${isDark
                                             ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                             : 'bg-purple-500 hover:bg-purple-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePurchaseStatusChange('PURCHASE_ONLY')}
                                       style={getIssueFocusStyles(4)}
                                       onFocus={() => setCurrentIssueFocusIndex(4)}>
                                       PURCHASE ONLY
                                    </Button>
                                    <Button
                                       ref={purchaseReturnRef}
                                       variant={filters.purchaseStatus === 'PURCHASE_RETURN' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.purchaseStatus === 'PURCHASE_RETURN'
                                          ? `${isDark
                                             ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                             : 'bg-purple-500 hover:bg-purple-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePurchaseStatusChange('PURCHASE_RETURN')}
                                       style={getIssueFocusStyles(5)}
                                       onFocus={() => setCurrentIssueFocusIndex(5)}>
                                       PURCHASE RETURN
                                    </Button>
                                    <Button
                                       ref={issueReturnRef}
                                       variant={filters.purchaseStatus === 'ISSUE_RETURN' ? 'solid' : 'outline'}
                                       size="sm"
                                       className={`rounded-full transition-all duration-200 ${filters.purchaseStatus === 'ISSUE_RETURN'
                                          ? `${isDark
                                             ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                             : 'bg-purple-500 hover:bg-purple-600 text-white'
                                          } shadow-md`
                                          : `${isDark
                                             ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                             : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                          }`
                                          }`}
                                       onClick={() => handlePurchaseStatusChange('ISSUE_RETURN')}
                                       style={getIssueFocusStyles(6)}
                                       onFocus={() => setCurrentIssueFocusIndex(6)}>
                                       ISSUE RETURN
                                    </Button>
                                 </>
                              ) : null}
                           </Flex>
                        </Stack>
                     </div>
                  )}

                  {/* Date Range */}
                  <div
                     className={`px-4 p-3 rounded-xl ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-white border border-gray-200'
                        } shadow-sm`}>
                     <Stack spacing={3}>
                        <Text
                           className={`font-semibold text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'
                              } uppercase tracking-wide flex items-center gap-2`}>
                           <span className={`w-1 h-4 rounded-full ${isDark ? 'bg-green-500' : 'bg-green-600'}`}></span>
                           Date Range
                        </Text>
                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                           <FormControl>
                              <FormLabel
                                 className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                                 From Date
                              </FormLabel>
                              <Input
                                 ref={fromDateRef}
                                 type="date"
                                 name="fromDate"
                                 value={filters.fromDate}
                                 onChange={handleDateChange}
                                 className={`rounded-lg ${isDark
                                    ? 'bg-gray-800 border-gray-600 text-gray-200'
                                    : 'bg-gray-50 border-gray-300 text-gray-800'
                                    } transition-all duration-200`}
                                 focusBorderColor="blue.500"
                                 style={getFocusStyles(entityType === 'receivemaster' ? 4 : 3)}
                                 onFocus={() => setCurrentFocusIndex(entityType === 'receivemaster' ? 4 : 3)}
                              />
                           </FormControl>
                           <FormControl>
                              <FormLabel
                                 className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                                 To Date
                              </FormLabel>
                              <Input
                                 ref={toDateRef}
                                 type="date"
                                 name="toDate"
                                 value={filters.toDate}
                                 onChange={handleDateChange}
                                 className={`rounded-lg ${isDark
                                    ? 'bg-gray-800 border-gray-600 text-gray-200'
                                    : 'bg-gray-50 border-gray-300 text-gray-800'
                                    } transition-all duration-200`}
                                 focusBorderColor="blue.500"
                                 style={getFocusStyles(entityType === 'receivemaster' ? 5 : 4)}
                                 onFocus={() => setCurrentFocusIndex(entityType === 'receivemaster' ? 5 : 4)}
                              />
                           </FormControl>
                        </Grid>
                     </Stack>
                  </div>

                  <div
                     className={`px-4 p-3 rounded-xl ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-white border border-gray-200'
                        } shadow-sm`}>
                     <Stack spacing={3}>
                        <Text
                           className={`font-semibold text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'
                              } uppercase tracking-wide flex items-center gap-2`}>
                           <span className={`w-1 h-4 rounded-full ${isDark ? 'bg-green-500' : 'bg-green-600'}`}></span>
                           Sort By
                        </Text>
                        <Flex gap={4}>
                           {entityType !== 'receivemaster' && (
                              <Button
                                 variant="outline"
                                 size="sm"
                                 className={`flex-1 rounded-lg transition-all duration-200 ${sortBy === 'pendingamt'
                                    ? `${isDark
                                       ? 'bg-blue-600 border-blue-600 text-white'
                                       : 'bg-blue-500 border-blue-500 text-white'
                                    }`
                                    : `${isDark
                                       ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                       : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`
                                    }`}
                                 onClick={() => handleSortChange('pendingamt')}
                                 rightIcon={
                                    sortBy === 'pendingamt' ? (
                                       sortOrder === 'ASC' ? (
                                          <svg
                                             className="w-4 h-4"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor">
                                             <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 15l7-7 7 7"
                                             />
                                          </svg>
                                       ) : (
                                          <svg
                                             className="w-4 h-4"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor">
                                             <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                             />
                                          </svg>
                                       )
                                    ) : null
                                 }>
                                 Pending Amount
                              </Button>
                           )}
                           <Button
                              variant="outline"
                              size="sm"
                              className={`flex-1 rounded-lg transition-all duration-200 ${sortBy === 'dueDayCount'
                                 ? `${isDark
                                    ? 'bg-blue-600 border-blue-600 text-white'
                                    : 'bg-blue-500 border-blue-500 text-white'
                                 }`
                                 : `${isDark
                                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                 }`
                                 }`}
                              onClick={() => handleSortChange('dueDayCount')}
                              rightIcon={
                                 sortBy === 'dueDayCount' ? (
                                    sortOrder === 'ASC' ? (
                                       <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path
                                             strokeLinecap="round"
                                             strokeLinejoin="round"
                                             strokeWidth={2}
                                             d="M5 15l7-7 7 7"
                                          />
                                       </svg>
                                    ) : (
                                       <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path
                                             strokeLinecap="round"
                                             strokeLinejoin="round"
                                             strokeWidth={2}
                                             d="M19 9l-7 7-7-7"
                                          />
                                       </svg>
                                    )
                                 ) : null
                              }>
                              Due Days
                           </Button>
                        </Flex>
                        <Text className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} italic`}>
                           Click to sort by {sortBy === 'pendingamt' ? 'Pending Amount' : 'Due Days'} in{' '}
                           {sortOrder === 'ASC' ? 'ascending' : 'descending'} order
                        </Text>
                     </Stack>
                  </div>

                  {/* Doc No */}
                  <div
                     className={`px-4 p-3 rounded-xl ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-white border border-gray-200'
                        } shadow-sm`}>
                     <Stack spacing={3}>
                        <FormControl>
                           <FormLabel
                              className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'
                                 } mb-2 flex items-center gap-2`}>
                              <span
                                 className={`w-1 h-4 rounded-full ${isDark ? 'bg-yellow-500' : 'bg-yellow-600'
                                    }`}></span>
                              Doc No
                           </FormLabel>
                           <Input
                              ref={docNoRef}
                              name="docno"
                              placeholder="Enter document number"
                              value={filters.docno}
                              onChange={handleChange}
                              className={`rounded-lg ${isDark
                                 ? 'bg-gray-800 border-gray-600 text-gray-200 placeholder-gray-500'
                                 : 'bg-gray-50 border-gray-300 text-gray-800 placeholder-gray-400'
                                 } transition-all duration-200`}
                              focusBorderColor="blue.500"
                           />
                        </FormControl>
                     </Stack>
                  </div>

                  {/* Daybook Dropdown */}
                  <div
                     className={`px-4 p-3 rounded-xl ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-white border border-gray-200'
                        } shadow-sm`}>
                     <Stack spacing={3}>
                        <Text
                           className={`font-semibold text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'
                              } uppercase tracking-wide flex items-center gap-2`}>
                           <span
                              className={`w-1 h-4 rounded-full ${isDark ? 'bg-indigo-500' : 'bg-indigo-600'}`}></span>
                           Daybook
                        </Text>
                        <FormControl>
                           <div
                              ref={daybookRef}
                              tabIndex={0}
                              style={getFocusStyles(entityType === 'receivemaster' ? 6 : 5)}
                              onFocus={() => setCurrentFocusIndex(entityType === 'receivemaster' ? 6 : 5)}>
                              {renderDynamicDropdown(
                                 'daybookIds',
                                 'Daybook',
                                 daybooks.filter((dy) => dy.type === daybookType),
                                 {
                                    value: daybookIds,
                                    onChange: handleDaybookChange,
                                    focusBorderColor: 'blue.500',
                                    _focus: {
                                       boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                                    },
                                 },
                                 errors,
                                 textColor,
                                 false,
                                 'Select Daybook',
                                 true,
                                 undefined,
                                 true,
                              )}
                           </div>
                        </FormControl>
                     </Stack>
                  </div>

                  {/* Account Dropdown */}
                  <div
                     className={`px-4 p-3 rounded-xl ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-white border border-gray-200'
                        } shadow-sm`}>
                     <Stack spacing={3}>
                        <Text
                           className={`font-semibold text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'
                              } uppercase tracking-wide flex items-center gap-2`}>
                           <span className={`w-1 h-4 rounded-full ${isDark ? 'bg-pink-500' : 'bg-pink-600'}`}></span>
                           Account
                        </Text>
                        <FormControl>
                           <div
                              ref={accountRef}
                              tabIndex={0}
                              style={getFocusStyles(entityType === 'receivemaster' ? 7 : 6)}
                              onFocus={() => setCurrentFocusIndex(entityType === 'receivemaster' ? 7 : 6)}>
                              {/* {renderDynamicDropdown(
                                 'accountIds',
                                 'Party',
                                 accounts,
                                 {
                                    value: accountIds,
                                    onChange: handleAccountChange,
                                    focusBorderColor: 'blue.500',
                                    _focus: {
                                       boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                                    },
                                    isMultiSelect: true,
                                 },
                                 errors,
                                 textColor,
                                 false,
                                 'Select Party',
                                 true,
                                 undefined,
                                 true,
                              )} */}
                              <MultiSelectDropdown
                                 label="Account"
                                 options={accounts}
                                 value={accountIds}
                                 onChange={handleAccountChange}
                                 placeholder="Select Account"
                                 isRequired={true}
                                 showAddButton={false}
                                 onAddNew={(value) => console.log('handleAddNew', value)}
                                 isLoading={false}
                                 darkMode={!isColorMode}
                                 AddEditFormComponent={AddEditForm}
                                 error={''}
                              />
                           </div>
                        </FormControl>
                     </Stack>
                  </div>
               </VStack>
            </ModalBody>

            <ModalFooter
               className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                  } border-t rounded-b-2xl`}>
               <Flex gap={3} width="100%">
                  <Button
                     ref={clearButtonRef}
                     onClick={handleClear}
                     className={`flex-1 rounded-xl font-semibold transition-all duration-200 ${isDark
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border-gray-600'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border-gray-300'
                        } border shadow-sm`}
                     variant="outline"
                     style={getFocusStyles(entityType === 'receivemaster' ? 8 : 7)}
                     onFocus={() => setCurrentFocusIndex(entityType === 'receivemaster' ? 8 : 7)}>
                     <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                     </svg>
                     Reset Filters
                  </Button>
                  <Button
                     ref={applyButtonRef}
                     onClick={handleApply}
                     className={`flex-1 rounded-xl font-semibold transition-all duration-200 shadow-lg ${isDark
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                        : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                        } text-white`}
                     colorScheme="blue"
                     style={getFocusStyles(entityType === 'receivemaster' ? 9 : 8)}
                     onFocus={() => setCurrentFocusIndex(entityType === 'receivemaster' ? 9 : 8)}>
                     <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                     </svg>
                     Apply Filters
                  </Button>
               </Flex>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default SalesInvoiceFilterModal;
